import React, { useState } from "react";
import Layout from "../../components/layout/Layout";
import Navbar from "../../components/layout/header/Navbar";
import Footer from "../../components/layout/footer/Footer";
import PageHeader from "../../components/team/PageHeader";
import emailjs from "emailjs-com";

export default function DiagnosticStudyIntake({ information }) {
  const [formState, setFormState] = useState({
    fullName: "",
    phone: "",
    email: "",
    preferredContact: "",
    consent: false,
  });
  const [statusMessage, setStatusMessage] = useState("");
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setAlert({ show: false, type: "", message: "" });

    if (!formState.consent) {
      setStatusMessage(
        "Please provide consent to allow the research team to contact you."
      );
      return;
    }
    setIsLoading(true);
    const payload = {
      fullName: formState.fullName,
      phone: formState.phone,
      email: formState.email,
      preferredContact: formState.preferredContact,
      status: "diagnostic-study-intake",
      message:
        "Diagnostic research study intake form submission. Preferred contact: " +
        (formState.preferredContact || "Not specified"),
    };

    emailjs
      .send("service_zgh2ksd", "template_cokkqp7", payload, "USX72MTx26hcTNM95")
      .then(() => {
        setAlert({
          show: true,
          type: "success",
          message:
            "Thank you. The research team will review your request shortly.",
        });
        setFormState({
          fullName: "",
          phone: "",
          email: "",
          preferredContact: "",
          consent: false,
        });
        setStatusMessage("");
      })
      .catch(() => {
        setAlert({
          show: true,
          type: "danger",
          message: "Failed to send. Please try again or contact us directly.",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Layout>
      <Navbar darkBg information={information} />
      <PageHeader HeaderTitle="Diagnostic Research Study" Parent="Contact Us" />

      <section className="ptb-60">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="mb-4">
                <h2 className="mb-3">Contact the Research Team</h2>
                <p className="lead mb-3">
                  This page allows individuals to connect with the research team
                  regarding an optional diagnostic research study. Completing
                  the form below does not schedule clinical care and does not
                  replace medical treatment. Participation is voluntary.
                </p>
                <p className="mb-0">
                  Eligibility screening and study visit scheduling are completed
                  directly with the research team and are separate from routine
                  clinical care.
                </p>
              </div>

              {alert.show && (
                <div
                  className={`alert alert-${alert.type} alert-dismissible fade show`}
                  role="alert"
                >
                  {alert.message}
                  <button
                    type="button"
                    className="close"
                    onClick={() =>
                      setAlert({ show: false, type: "", message: "" })
                    }
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-us-form">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="d-block">Full Name*</label>
                      <input
                        type="text"
                        name="fullName"
                        className="form-control"
                        value={formState.fullName}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="d-block">Phone Number*</label>
                      <input
                        type="tel"
                        name="phone"
                        className="form-control"
                        value={formState.phone}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="d-block">
                        Email Address (optional)
                      </label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        value={formState.email}
                        onChange={handleChange}
                        placeholder="name@example.com"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="d-block">
                        Preferred Method of Contact
                      </label>
                      <select
                        name="preferredContact"
                        className="form-control"
                        value={formState.preferredContact}
                        onChange={handleChange}
                        required
                        disabled={isLoading}
                      >
                        <option value="" disabled>
                          Select an option
                        </option>
                        <option value="Call">Call</option>
                        <option value="Text">Text</option>
                        <option value="Email">Email</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-group form-check mb-4">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="consentCheckbox"
                    name="consent"
                    checked={formState.consent}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                  />
                  <label className="form-check-label" htmlFor="consentCheckbox">
                    I give permission for the research team to contact me
                    regarding eligibility screening and potential scheduling of
                    a research study visit.*
                  </label>
                </div>

                <button
                  type="submit"
                  className="btn solid-btn"
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : "Submit"}
                </button>

                {statusMessage && (
                  <p className="mt-3 mb-0 text-muted">{statusMessage}</p>
                )}
              </form>

              <div className="mt-4">
                <h5>Prefer to contact us directly?</h5>
                <p className="mb-4">
                  Call or text the research team at{" "}
                  <a href="tel:2067750598">206-775-0598</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer space information={information} />
    </Layout>
  );
}
